import { useEffect, useState } from 'react';

import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAddMembersRequestMutation, useFindUsersRequestMutation } from 'apis/organization-units.api';
import { ADD_NEW_MEMBER_TABLE_COLUMNS } from 'constants/organization-units';
import { useOpen } from 'hooks/useOpen';
import { usePagination } from 'hooks/usePagination';
import { useTableSorting } from 'hooks/useTableSorting';
import {
  AddNewMembersPayload,
  ExtendedAddNewMembersPayload,
  SelectedUnitInfo,
} from 'interfaces/organization-units.interface';
import { CommonError } from 'interfaces/shared.interface';
import { Button, ErrorModal, Input, LoadingOverlay, Modal, Pagination, Table } from 'shared-components';
import ModalControlButtons from 'shared-components/ModalControlButtons';
import { successNotify } from 'utils/notifications';

import AddMemberModalRow from './AddMemberModalRow';

interface Props {
  handleClose: VoidFunction;
  refetchListRoot: VoidFunction;
  isOpen: boolean;
  selectedUnit: SelectedUnitInfo | null;
}

const AddMemberModal = ({ handleClose, isOpen, refetchListRoot, selectedUnit }: Props) => {
  const { t } = useTranslation();
  const [addMembersRequest] = useAddMembersRequestMutation();
  const [isErrorModalOpen, handleOpenErrorModal, handleCloseErrorModal] = useOpen();
  const { handleSetTabName, sortingColumnId, sortingType } = useTableSorting();
  const { handleSetMaxResultCount, maxResultCount, handleSetCurrentPage, currentPage } = usePagination();
  const [findUsers, { data, isLoading: isFindUsersLoading, isUninitialized }] = useFindUsersRequestMutation({});
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const { result } = data || {};

  const addMembersFormMethods = useForm<ExtendedAddNewMembersPayload>({
    defaultValues: {
      search: '',
    },
  });

  const handleSearchChange = addMembersFormMethods.handleSubmit(() => {
    fetchUsers();
  });

  const {
    watch,
    register,
    reset,
    formState: { errors },
  } = addMembersFormMethods;

  const { data: errorData } = (errors as CommonError) || {};
  const searchQuery = watch('search');

  const onCloseModal = () => {
    reset();
    setSelectedUserIds([]);
    handleClose();
  };

  const fetchUsers = () => {
    findUsers({
      filter: searchQuery,
      maxResultCount: '10',
      organizationUnitId: selectedUnit?.id,
      skipCount: 0,
    });
  };

  const onSubmit = async () => {
    const payload: AddNewMembersPayload = {
      organizationUnitId: selectedUnit?.id,
      userIds: selectedUserIds,
    };
    try {
      await addMembersRequest(payload).unwrap();
      refetchListRoot();
      onCloseModal();
      successNotify(t('SuccessfullyAdded'));
    } catch (error) {
      handleOpenErrorModal();
    }
  };

  const handleToggleUserSelect = (userId: number) => {
    setSelectedUserIds((prevSelectedIds) =>
      prevSelectedIds.includes(userId) ? prevSelectedIds.filter((id) => id !== userId) : [...prevSelectedIds, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUserIds([]);
    } else {
      const allUserIds = result?.items.map((item) => Number(item.value)) || [];
      setSelectedUserIds(allUserIds);
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    isOpen && fetchUsers();
  }, [isOpen]);

  useEffect(() => {
    const allUserIds = result?.items?.map((item) => Number(item.value)) || [];
    const areAllSelected = allUserIds.length > 0 && allUserIds.every((id) => selectedUserIds.includes(id));
    setSelectAll(areAllSelected);
  }, [result?.items, selectedUserIds]);

  return (
    <>
      <Modal
        dataCy="create-root-unit-modal"
        isOpen={isOpen}
        isDisableOutsideClick
        onClose={onCloseModal}
        heading="SelectUsers"
      >
        <FormProvider {...addMembersFormMethods}>
          <form data-cy="create-root-unit-modal-form" onSubmit={addMembersFormMethods.handleSubmit(onSubmit)}>
            <div className="flex w-full">
              <Input
                isLoading={isFindUsersLoading}
                {...register('search')}
                dataCy="add-member-search"
                className="mb-3.5 flex-grow"
                inputClassName="w-full text-sm"
                name="search"
                placeholder="SearchWithThreeDot"
              />
              <Button
                dataCy="submit-search-filter"
                onClick={handleSearchChange}
                type="submit"
                className="h-10 rounded-s-none"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </Button>
            </div>

            <div className="relative">
              <LoadingOverlay isLoading={isUninitialized} />

              <Table
                dataCy="add-member-table"
                columns={ADD_NEW_MEMBER_TABLE_COLUMNS}
                handleSetTabName={handleSetTabName}
                sortingColumnId={sortingColumnId}
                isTableEmpty={!result?.items.length}
                sortingType={sortingType}
                isLoading={isFindUsersLoading}
                selectAll={selectAll}
                handleSelectAll={handleSelectAll}
                isSelectAllCheckbox
              >
                {result?.items.map((item) => (
                  <AddMemberModalRow
                    key={item.value}
                    memberData={item}
                    onToggleSelect={() => handleToggleUserSelect(Number(item.value))}
                    isSelected={selectedUserIds.includes(Number(item.value))}
                  />
                ))}
              </Table>

              <Pagination
                totalCount={result?.totalCount}
                handleSetMaxResultCount={handleSetMaxResultCount}
                maxResultCount={maxResultCount}
                handleSetCurrentPage={handleSetCurrentPage}
                currentPage={currentPage}
              />
            </div>

            <ModalControlButtons isShownSubmit className="pb-5" isLoading={isFindUsersLoading} onClose={onCloseModal} />
          </form>
        </FormProvider>
      </Modal>
      <ErrorModal
        errorMessage={errorData?.error?.message}
        description={errorData?.error?.details}
        isOpen={isErrorModalOpen}
        handleClose={handleCloseErrorModal}
      />
    </>
  );
};

export default AddMemberModal;
